import { Component, inject, OnInit, signal } from '@angular/core';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../@shared/components/table-component/table-component';
import { ButtonModule } from 'primeng/button';
import { Customer } from '../../../models/customer';
import { enumToOptions } from '../../../@shared/utility/enumToOptions';
import { CustomerStatus } from '../../../models/enums';
import { FormControl } from '@angular/forms';
import { CustomerService } from '../../../services/customers/customer-service';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { PaginatorState } from 'primeng/paginator';
import { Params } from '../../../services/utilis/add-params-service';
import { UpdateCustomerForm } from '../../../@shared/forms/customers/update-customer-form/update-customer-form';
import { DeleteCustomerForm } from '../../../@shared/forms/customers/delete-customer-form/delete-customer-form';
import { Employee } from '../../../models/employee';
import { EmployeeService } from '../../../services/employees/employee-service';
import { Card } from 'primeng/card';


@Component({
  selector: 'app-customers-list',
  imports: [TableComponent, ButtonModule, UpdateCustomerForm, DeleteCustomerForm,Card],
  templateUrl: './customers-list.html',
  styleUrl: './customers-list.scss'
})
export class CustomersList implements OnInit {

  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
    'status',
    'minAvgFeedback',
    'maxAvgFeedback',
    'createdFrom',
    'createdTo',
    'onboardingCompleted',

  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    status: "status",
    minAvgFeedback: "minAvgFeedback",
    maxAvgFeedback: "maxAvgFeedback",
    createdFrom: "createdFrom",
    createdTo: "createdTo",
    onboardingCompleted: "onboardingCompleted",
  }

  // Define columns to show in the table
  tableColumns: DynamicColumn<Customer>[] = [
    {
      header: 'ID',
      field: 'id',
      sortable: true,
      valueGetter: (c) => '#' + c.id,
      isCopy: true,
      command: (customer) => this.redirectToCustomerDetailsPage(customer),
      cellClass: 'cursor-pointer'
    },
    {
      header: 'Customer',
      field: 'firstName',
      mergedName: 'lastName',
      bottomField: 'email',
      isAvatar: true,
      avatarImageField: 'imageUrl',
      sortable: true,
    },
    { header: 'Phone', field: 'phoneNumber', sortable: false },
    { header: 'Register Date', field: 'createdAt', sortable: true, isDateTime: true },
    {
      header: 'Wallet Balance',
      field: 'walletBalance',
      sortable: true,
      isCurrency: true,
      cellClass: (value) => (value as number) < 0 ? 'text-red-500' : '',
    },
    { header: 'Points', field: 'points', sortable: true },
    {
      header: 'Feedback',
      field: 'averageFeedbackRating',
      sortable: false,
      isRating: true,
      isFilterable: true,
      filterType: 'slider',
      sliderMinKey: 'minAvgFeedback',
      sliderMaxKey: 'maxAvgFeedback',
      min: 1,
      max: 5,
      step: 1,
    },
    {
      header: 'Status',
      field: 'status',
      sortable: true,
      isStatusBadge: true,
      badgeStyleGetter: (v) => this.customerStatusBadge(v as string),
    },
  ];

  customerStatusBadge(value: string): { background: string; color: string } {
    const map: Record<string, { background: string; color: string }> = {
      'ACTIVE':                     { background: '#dcfce7', color: '#16a34a' },
      'BANNED':                     { background: '#fee2e2', color: '#dc2626' },
      'AWAITING_OTP_VERIFICATION':  { background: '#fef9c3', color: '#854d0e' },
      'AWAITING_PAYMENT':           { background: '#fef9c3', color: '#854d0e' },
      'PAYMENT_FAILED':             { background: '#fee2e2', color: '#dc2626' },
      'AWAITING_DOCUMENT_UPLOAD':   { background: '#ffedd5', color: '#c2410c' },
      'AWAITING_DOCUMENT_REVIEW':   { background: '#ffedd5', color: '#c2410c' },
      'DOCUMENTS_REJECTED':         { background: '#fee2e2', color: '#dc2626' },
    };
    return map[value] ?? { background: '#f1f5f9', color: '#64748b' };
  }

  customersList = signal<Customer[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  searchControl = new FormControl('');

  employee = signal<Employee | undefined>(undefined);

  selectedCustomer: Customer
  updateCustomerDialog = false
  deleteCustomerDialog = false

  statusFilter: CustomFilter = {
    key: 'status',
    label: 'Status',
    type: 'multiselect',
    options: enumToOptions(CustomerStatus),
  };

  onboardingFilter: CustomFilter = {
    key: 'onboardingCompleted',
    label: 'Onboarding',
    type: 'chip',
    options: [
      { label: 'Completed', value: 'true' },
      { label: 'Pending', value: 'false' },
    ],
  };

  createdDateFilter: CustomFilter = {
    key: 'created',
    label: 'Date',
    type: 'date-range',
    options: [],
  };

  private customerService = inject(CustomerService)
  private employeeService = inject(EmployeeService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  ngOnInit() {
    this.getCustomersList()
    this.getEmployee()
  }

  getCustomersList = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'updatedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.customerService.getCustomersList(params);
      })
    )
      .subscribe((res) => {
        this.customersList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  getEmployee = () => {
    this.employee = this.employeeService.employee;
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  openUpdateCustomerDialog = (selectedRow: Customer) => {
    this.selectedCustomer = selectedRow
    this.updateCustomerDialog = true
  }

  onCustomerUpdated = () => {
    this.getCustomersList()
    this.updateCustomerDialog = false
  }

  openDeleteCustomerDialog = (selectedRow: Customer) => {
    this.selectedCustomer = selectedRow
    this.deleteCustomerDialog = true
  }

  onCustomerDeleted = () => {
    this.getCustomersList()
    this.deleteCustomerDialog = false
  }

  redirectToCustomerDetailsPage = (customer: Customer) => {
    this.router.navigate([`/customers/${customer.id}`])
  }

  customerRowLink = (customer: Customer) => `/customers/${customer.id}`;
}
